import { t } from '../i18n';
import { useEffect, type FC } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PdfReader } from './LazyPdfReader';

interface MaterialViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  materialUrl: string | null;
  title?: string;
}

export const MaterialViewerModal: FC<MaterialViewerModalProps> = ({
  isOpen,
  onClose,
  materialUrl,
  title = t('Dars materiali'),
}) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Escape bilan yopish — modal uchun zarur, ilgari yo'q edi.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !materialUrl) return null;

  const isPdf = materialUrl.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(materialUrl);
  const isOffice = /\.(doc|docx|ppt|pptx|xls|xlsx)$/i.test(materialUrl);

  const absoluteUrl = materialUrl.startsWith('http')
    ? materialUrl
    : `${window.location.origin}${materialUrl.startsWith('/') ? '' : '/'}${materialUrl}`;

  let content;
  if (isPdf || isImage) {
    /*
     * Ilgari bu <iframe src={pdf}> edi. Telegram'ning ichki brauzerida
     * (ayniqsa iOS) bunday iframe PDF'ni faqat birinchi sahifasini
     * ko'rsatadi va SCROLL BERMAYDI — o'quvchi materialni oxirigacha
     * o'qiy olmasdi. Endi PDF o'zimizning o'qiluvchimizda chiziladi:
     * tabiiy scroll, masshtab, sahifa hisoblagichi.
     */
    content = (
      <PdfReader
        url={materialUrl}
        maxHeight="100%"
        onOpenExternally={(u) => {
          if (window.Telegram?.WebApp?.openLink) {
            window.Telegram.WebApp.openLink(u);
          } else {
            window.open(u, '_blank');
          }
        }}
      />
    );
  } else if (isOffice) {
    content = (
      <iframe
        src={`https://docs.google.com/gview?url=${encodeURIComponent(absoluteUrl)}&embedded=true`}
        title={title}
        width="100%"
        height="100%"
        style={{ border: 'none', background: '#fff' }}
      />
    );
  } else {
    content = (
      <div
        className="empty"
        style={{ height: '100%', border: 'none', borderRadius: 0 }}
      >
        <div className="empty-title">
          {t('Bu formatni bu yerda ochib boʻlmaydi')}
        </div>
        <a
          href={materialUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ marginTop: 'var(--space-3)' }}
        >
          {t('Faylni ochish')}
        </a>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
        }}
      >
        <div
          className="spread"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            paddingTop: 'calc(var(--space-3) + env(safe-area-inset-top, 0px))',
            background: 'var(--surface)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div className="row" style={{ minWidth: 0 }}>
            <h2
              className="topbar-title"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </h2>
            <a
              href={materialUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('Yangi oynada ochish')}
              style={{ display: 'flex', flex: 'none' }}
            >
              <ExternalLink size={15} aria-hidden="true" />
            </a>
          </div>

          <button
            type="button"
            className="theme-toggle"
            onClick={onClose}
            aria-label={t('Yopish')}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', background: '#fff' }}>
          {content}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
